import { Box, CircularProgress, Typography } from "@mui/material";
import { Sparkles } from "lucide-react";

import { Badge } from "../../../components/Badge";
import { brand } from "../../../theme/theme";
import { PlatformIcon, getPlatformMeta, isDocumentMedia, isVideoMedia } from "../workflow/platformMeta";
import { DurationBadge, VideoPoster } from "../workflow/VideoMedia";
import { markdownToPlain } from "../workflow/workflowUtils";
import { STATE_CHIP, panelSx } from "./campaignUi";

const tileMinHeight = 230;

/** One piece of the batch in the "Piezas generadas" grid. */
export function PieceTile({ piece, state, index, onOpen }) {
  const adaptations = piece?.platform_adaptations || [];
  // A LinkedIn PDF has no picture: the tile shows the first image or video.
  const firstMedia = adaptations.flatMap((a) => a.images || []).find((item) => !isDocumentMedia(item)) || null;
  const isVideo = isVideoMedia(firstMedia);
  const image = firstMedia?.image;
  const platforms = adaptations.map((a) => a.platform);
  const chip = STATE_CHIP[state];
  const busy = state === "composing" || state === "adapting" || state === "queued";
  const preview = markdownToPlain(piece?.raw_content || "").slice(0, 90);

  if (!piece || state === "composing" || state === "queued") {
    return (
      <Box
        sx={{
          ...panelSx,
          borderStyle: state === "queued" ? "dashed" : "solid",
          backgroundColor: state === "composing" ? "#FDF5F9" : "#fff",
          borderColor: state === "composing" ? brand.softBorder : "#E8ECF2",
          p: 2,
          minHeight: tileMinHeight,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 2.5, flex: 1, justifyContent: "center" }}>
          {state === "composing" ? (
            <CircularProgress size={28} sx={{ color: brand.pink }} />
          ) : (
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: brand.gradientDiagonal, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sparkles size={18} color="#fff" />
            </Box>
          )}
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: brand.pinkDark }}>
            {state === "composing" ? "Componiendo..." : `Pieza ${index + 1} en cola`}
          </Typography>
          {piece?.title && (
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151", textAlign: "center" }}>
              {piece.title}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Without `onOpen` the tile is read-only (batch pieces are managed from the table); busy pieces too.
  const clickable = Boolean(onOpen) && !busy;

  return (
    <Box
      {...(clickable ? { component: "button", type: "button", onClick: () => onOpen(piece) } : {})}
      sx={{
        ...panelSx,
        p: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        font: "inherit",
        minHeight: tileMinHeight,
        ...(clickable && {
          cursor: "pointer",
          transition: "box-shadow 0.15s, transform 0.15s",
          "&:hover": { boxShadow: "0 8px 24px rgba(15,23,42,0.08)", transform: "translateY(-1px)" },
          "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
        }),
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 104,
          background: image || isVideo ? "#F3F4F6" : `linear-gradient(135deg, ${brand.soft} 0%, #FFF4E5 100%)`,
          display: "flex",
          alignItems: "flex-end",
          p: 1.25,
        }}
      >
        {isVideo ? (
          <>
            <VideoPoster src={image} playSize={34} />
            <DurationBadge seconds={firstMedia.duration} sx={{ left: "auto", right: 8 }} />
          </>
        ) : image ? (
          <Box component="img" src={image} alt="" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Typography sx={{ fontSize: "0.75rem", color: "#9A6B82", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {preview}
          </Typography>
        )}
        {platforms.length > 0 && (
          <Box sx={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 0.5, px: 0.75, py: 0.5, borderRadius: "999px", backgroundColor: "rgba(255,255,255,0.94)" }}>
            {platforms.slice(0, 4).map((platform) => (
              <PlatformIcon key={platform} platform={platform} size={18} sx={{ backgroundColor: "transparent" }} />
            ))}
            {platforms.length > 4 && (
              <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, color: "#374151" }}>+{platforms.length - 4}</Typography>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 1.75, display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", color: "#111827", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {piece.title}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
          {platforms.length
            ? platforms.map((platform) => getPlatformMeta(platform).label).join(" · ")
            : `${(piece.raw_content || "").trim().split(/\s+/).filter(Boolean).length} palabras de texto base`}
        </Typography>
        <Box sx={{ mt: "auto", pt: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Badge color={chip.color}>
            {busy && <CircularProgress size={10} sx={{ color: "inherit", mr: 0.5 }} />}
            {chip.label}
          </Badge>
          {clickable && <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: brand.pink }}>Abrir</Typography>}
        </Box>
      </Box>
    </Box>
  );
}
